import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { Currency } from '../../types/currency';
import { Icon, IconType } from '../icons/Icon';
import styles from './CurrencySelector.module.less';

const DROPDOWN_CLOSE_DELAY = 200;

const getCurrencyIcon = (currency: Currency): IconType =>
  currency.toLowerCase() as IconType;

interface CurrencySelectorProps {
  selectedCurrency: Currency;
  onCurrencyChange: (currency: Currency) => void;
  availableCurrencies: Currency[];
}

export const CurrencySelector: React.FC<CurrencySelectorProps> = ({
  selectedCurrency,
  onCurrencyChange,
  availableCurrencies,
}) => {
  const intl = useIntl();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [slidingOutCurrency, setSlidingOutCurrency] = useState<Currency | null>(
    null
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonIconRef = useRef<HTMLDivElement>(null);
  const buttonTextRef = useRef<HTMLSpanElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  const calculateSlideDistances = (
    clickedElement: HTMLElement
  ): {
    icon: { deltaX: number; deltaY: number } | null;
    text: { deltaX: number; deltaY: number } | null;
  } => {
    const clickedIcon = clickedElement.querySelector(`.${styles.iconWrapper}`);
    const clickedText = clickedElement.querySelector(`.${styles.currencyText}`);
    const buttonIcon = buttonIconRef.current;
    const buttonText = buttonTextRef.current;

    let iconDistance: { deltaX: number; deltaY: number } | null = null;
    let textDistance: { deltaX: number; deltaY: number } | null = null;

    if (clickedIcon && buttonIcon) {
      const clickedIconRect = clickedIcon.getBoundingClientRect();
      const buttonIconRect = buttonIcon.getBoundingClientRect();
      iconDistance = {
        deltaX: buttonIconRect.left - clickedIconRect.left,
        deltaY: buttonIconRect.top - clickedIconRect.top,
      };
    }

    if (clickedText && buttonText) {
      const clickedTextRect = clickedText.getBoundingClientRect();
      const buttonTextRect = buttonText.getBoundingClientRect();
      textDistance = {
        deltaX: buttonTextRect.left - clickedTextRect.left,
        deltaY: buttonTextRect.top - clickedTextRect.top,
      };
    }

    return { icon: iconDistance, text: textDistance };
  };

  const handleCurrencySelect = (
    currency: Currency,
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    const itemElement = event.currentTarget;
    const distances = calculateSlideDistances(itemElement);

    if (distances.icon) {
      itemElement.style.setProperty(
        '--icon-slide-x',
        `${distances.icon.deltaX}px`
      );
      itemElement.style.setProperty(
        '--icon-slide-y',
        `${distances.icon.deltaY}px`
      );
    }

    if (distances.text) {
      itemElement.style.setProperty(
        '--text-slide-x',
        `${distances.text.deltaX}px`
      );
      itemElement.style.setProperty(
        '--text-slide-y',
        `${distances.text.deltaY}px`
      );
    }

    setSlidingOutCurrency(currency);
    onCurrencyChange(currency);

    setTimeout(() => {
      setIsDropdownOpen(false);
      setSlidingOutCurrency(null);
    }, DROPDOWN_CLOSE_DELAY);
  };

  return (
    <div className={styles.currencySelector} ref={dropdownRef}>
      <button
        className={styles.currencyButton}
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        type="button"
      >
        <div ref={buttonIconRef}>
          <Icon
            type={getCurrencyIcon(selectedCurrency)}
            className={styles.currencyIcon}
          />
        </div>
        <div className={styles.buttonText}>
          {intl.formatMessage({ id: 'form.currency.borrow' })}
          <span ref={buttonTextRef}>{selectedCurrency}</span>
        </div>
        <Icon
          type="chevronDown"
          className={`${styles.dropdownArrow} ${
            isDropdownOpen ? styles.rotated : ''
          }`}
        />
      </button>
      <div
        className={`${styles.dropdownMenu} ${
          isDropdownOpen ? styles.open : ''
        }`}
      >
        {availableCurrencies.map(currency => (
          <button
            key={currency}
            className={`${styles.dropdownItem} ${
              slidingOutCurrency === currency ? styles.slideOut : ''
            }`}
            onClick={e => handleCurrencySelect(currency, e)}
            type="button"
          >
            <div className={styles.currencyContent}>
              <div className={styles.iconWrapper}>
                <Icon
                  type={getCurrencyIcon(currency)}
                  className={styles.currencyIcon}
                />
              </div>
              <span className={styles.currencyText}>{currency}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
